import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { AuthUser, LoginResponse } from '@/types'

const baseURL = import.meta.env.VITE_API_URL || ''
console.log('baseURL:', baseURL);

const apiClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
})

/** Used for refresh/logout so we never attach a stale Bearer token. */
const bareClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
})

let refreshTimer: ReturnType<typeof setTimeout> | null = null
let refreshPromise: Promise<void> | null = null

function isAuthPublicPath(url: string | undefined): boolean {
  if (!url) return false
  return ['/auth/login', '/auth/refresh', '/auth/logout'].some(p => url.includes(p))
}

apiClient.interceptors.request.use((config) => {
  if (!isAuthPublicPath(config.url)) {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  } else {
    delete config.headers.Authorization
  }
  return config
})

/** Avoid storing garbage dates — `setItem(undefined)` becomes `"undefined"` and breaks scheduling. */
export function parseApiDateTimeToIso(value: unknown): string | null {
  if (value == null) return null
  if (typeof value === 'string') {
    const ms = Date.parse(value)
    return Number.isFinite(ms) ? new Date(ms).toISOString() : null
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value).toISOString()
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString()
  }
  return null
}

function updateStoredSessionFromLoginResponse(data: LoginResponse): void {
  localStorage.setItem('token', data.token)
  if (data.refreshToken) {
    localStorage.setItem('refreshToken', data.refreshToken)
  }

  const accessExp = parseApiDateTimeToIso((data as { expiresAt?: unknown }).expiresAt)
  if (accessExp) {
    localStorage.setItem('tokenExpiresAt', accessExp)
  }

  const rtExp = parseApiDateTimeToIso((data as { refreshTokenExpiresAt?: unknown }).refreshTokenExpiresAt)
  if (rtExp) {
    localStorage.setItem('refreshTokenExpiresAt', rtExp)
  }

  const userRaw = localStorage.getItem('user')
  if (!userRaw) return
  try {
    const u = JSON.parse(userRaw) as AuthUser
    u.token = data.token
    if (accessExp) u.expiresAt = accessExp
    if (data.refreshToken) u.refreshToken = data.refreshToken
    if (rtExp) u.refreshTokenExpiresAt = rtExp
    localStorage.setItem('user', JSON.stringify(u))
  } catch {
    // ignore
  }
}

async function performTokenRefresh(): Promise<void> {
  if (refreshPromise) {
    await refreshPromise
    return
  }

  const rt = localStorage.getItem('refreshToken')
  if (!rt) {
    throw new Error('No refresh token')
  }

  refreshPromise = (async () => {
    const { data } = await bareClient.post<LoginResponse>('/api/v1/auth/refresh', { refreshToken: rt })
    updateStoredSessionFromLoginResponse(data)
    window.dispatchEvent(new CustomEvent('report-studio:session-refreshed'))
  })().finally(() => {
    refreshPromise = null
  })

  await refreshPromise
}

/**
 * How long before access-token expiry we fire a proactive refresh.
 * A fixed 60s margin breaks when `Jwt:ExpirationMinutes` is 1: the token only lives ~60s,
 * so "refresh 60s before expiry" becomes `delay === 0` every time → a refresh storm.
 */
function proactiveSkewMs(remainingMs: number): number {
  if (remainingMs <= 0) return 0
  // 15% of remaining lifetime, clamped between 5s and 60s
  return Math.min(60_000, Math.max(5_000, Math.floor(remainingMs * 0.15)))
}

function msUntilProactiveRefresh(expIso: string): number | null {
  const expMs = Date.parse(expIso)
  if (!Number.isFinite(expMs)) return null
  const remainingMs = expMs - Date.now()
  const skewMs = proactiveSkewMs(remainingMs)
  return Math.max(0, remainingMs - skewMs)
}

/**
 * Schedules a single proactive refresh before access token expiry.
 * Next run is scheduled from `AuthContext` after `report-studio:session-refreshed` (not chained here)
 * so we do not double-schedule or hit `setTimeout(NaN)` → 0 ms loops.
 */
export function scheduleAccessTokenRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
  const expStr = localStorage.getItem('tokenExpiresAt')
  if (!expStr) return

  const delay = msUntilProactiveRefresh(expStr)
  if (delay === null) {
    return
  }

  refreshTimer = setTimeout(() => {
    void performTokenRefresh().catch(() => {
      clearAuthStorageAndRedirect()
    })
  }, delay)
}

export function clearRefreshSchedule(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

function clearAuthStorageAndRedirect(): void {
  clearRefreshSchedule()
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('tokenExpiresAt')
  localStorage.removeItem('refreshTokenExpiresAt')
  localStorage.removeItem('user')
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login'
  }
}

export async function revokeRefreshToken(refreshToken: string): Promise<void> {
  await bareClient.post('/api/v1/auth/logout', { refreshToken })
}

/**
 * With `responseType: 'blob'`, failed responses are still Blobs; `apiError` cannot read
 * ProblemDetails `detail` until we turn the body into JSON or text.
 */
async function materializeBlobErrorData(error: AxiosError): Promise<void> {
  const res = error.response
  if (!res?.data || !(res.data instanceof Blob)) return

  try {
    const text = await res.data.text()
    const trimmed = text.trim()
    if (!trimmed) {
      res.data = ''
      return
    }
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        res.data = JSON.parse(text) as unknown
        return
      } catch {
        // non-JSON despite braces — show raw snippet
      }
    }
    res.data = text
  } catch {
    // leave Blob if read fails
  }
}

apiClient.interceptors.response.use(
  res => res,
  async (error: AxiosError) => {
    await materializeBlobErrorData(error)

    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined
    const status = error.response?.status
    const url = original?.url ?? ''

    if (status !== 401 || !original || original._retry) {
      return Promise.reject(error)
    }

    if (isAuthPublicPath(url)) {
      clearAuthStorageAndRedirect()
      return Promise.reject(error)
    }

    original._retry = true
    try {
      await performTokenRefresh()
      const token = localStorage.getItem('token')
      if (token) {
        original.headers = original.headers ?? {}
        original.headers.Authorization = `Bearer ${token}`
      }
      return apiClient.request(original)
    } catch {
      clearAuthStorageAndRedirect()
      return Promise.reject(error)
    }
  }
)

export default apiClient
